import React, { useState, useCallback } from "react";
import {
  Button,
  Box,
  Modal,
  Typography,
  LinearProgress,
  IconButton,
} from "@mui/material";
import { useDropzone } from "react-dropzone";
import * as XLSX from "xlsx";
import axios from "../../api";
import { useSnackbar } from "components/AlertMessages/SnackbarContext";
import { CloudUpload, Download, Description, Close } from "@mui/icons-material";
import { AxiosError } from "axios";

const customScrollbarStyle = {
  "&::-webkit-scrollbar": { width: "8px" },
  "&::-webkit-scrollbar-thumb": {
    backgroundColor: "#44444461",
    borderRadius: "8px",
  },
};

interface AssetTypeRow {
  "Asset Type": string;
}
interface ParsedAssetType {
  asset_type_name: string;
}
interface BulkUploadAssetProps {
  isOpen: boolean;
  onClose: () => void;
  fetchData: () => Promise<void>;
}
const BulkUploadAssetType = ({
  isOpen,
  onClose,
  fetchData,
}: BulkUploadAssetProps) => {
  const { fetchError, fetchSuccess } = useSnackbar();
  const [uploading, setUploading] = useState<boolean>(false);
  const [progress, setProgress] = useState<number>(0);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [parsedData, setParsedData] = useState<ParsedAssetType[]>([]);

  const downloadTemplate = () => {
    const template = [
      {
        "Asset Type": "Example Type",
      },
    ];

    const ws = XLSX.utils.json_to_sheet(template);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Asset Type Template");

    ws["!cols"] = [{ wch: 25 }];

    XLSX.writeFile(wb, "asset_type_bulk_upload_template.xlsx");
  };

  const validateAndNormalize = (
    row: ParsedAssetType,
    index: number
  ): ParsedAssetType => {
    if (!row.asset_type_name) {
      throw new Error(`Row ${index + 1}: Missing required fields`);
    }
    return { ...row };
  };

  // Fixed: Wrapped async logic to avoid returning a Promise to onDrop
  const onDrop = useCallback(
    (acceptedFiles: File[]) => {
      const file = acceptedFiles[0];
      if (!file) return;

      setSelectedFile(file);

      // Process the file asynchronously without returning the Promise
      (async () => {
        try {
          const arrayBuffer = await file.arrayBuffer();

          const data = new Uint8Array(arrayBuffer);
          const workbook = XLSX.read(data, { type: "array" });
          const sheetName = workbook.SheetNames[0];
          const worksheet = workbook.Sheets[sheetName];
          const jsonData = XLSX.utils.sheet_to_json<AssetTypeRow>(worksheet);

          const normalizedData: ParsedAssetType[] = jsonData.map((row) => ({
            asset_type_name: row["Asset Type"] ?? "",
          }));

          const validatedData = normalizedData.map(validateAndNormalize);
          setParsedData(validatedData);
        } catch (error) {
          if (error instanceof Error) {
            fetchError(
              error.message ?? "Error parsing file. Check format and fields."
            );
          } else {
            fetchError("Error parsing file. Check format and fields.");
          }
          setSelectedFile(null);
        }
      })();
    },
    [fetchError]
  );

  const handleUpload = async () => {
    if (!parsedData.length) {
      fetchError("No file data found. Please upload a valid Excel/CSV file.");
      return;
    }

    setUploading(true);
    setProgress(20);

    try {
      const response = await axios.post("/asset-types/bulk-upload", {
        assetTypes: parsedData,
      });

      setProgress(100);
      fetchSuccess(
        response.data.message ?? "Asset Types uploaded successfully!"
      );
      fetchData?.();

      setTimeout(() => handleClose(), 1500);
    } catch (error) {
      console.error(error);
      if (error instanceof AxiosError && error.response?.data?.message) {
        fetchError(error.response.data.message);
      } else if (error instanceof Error) {
        fetchError(error.message);
      } else {
        fetchError("Upload failed. Please check the file and try again.");
      }
    } finally {
      setUploading(false);
      setProgress(0);
    }
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": [
        ".xlsx",
      ],
      "application/vnd.ms-excel": [".xls"],
      "text/csv": [".csv"],
    },
    multiple: false,
  });

  const handleClose = () => {
    setProgress(0);
    setUploading(false);
    setParsedData([]);
    setSelectedFile(null);
    onClose();
  };

  const handleRemoveFile = () => {
    setSelectedFile(null);
    setParsedData([]);
  };

  return (
    <Modal open={isOpen} onClose={handleClose}>
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          bgcolor: "background.paper",
          borderRadius: "8px",
          boxShadow: 24,
          p: 4,
          minWidth: 600,
          maxWidth: "90vw",
          textAlign: "center",
          ...customScrollbarStyle,
        }}
      >
        <Typography variant="h5" sx={{ mb: 2 }}>
          Bulk Upload Asset Types
        </Typography>

        <Button
          variant="outlined"
          startIcon={<Download />}
          onClick={downloadTemplate}
          sx={{ mb: 3 }}
        >
          Download Template
        </Button>

        <Box
          {...getRootProps()}
          sx={{
            border: "2px dashed #ccc",
            borderRadius: "8px",
            p: 4,
            cursor: "pointer",
            backgroundColor: isDragActive ? "#f0f0f0" : "transparent",
            "&:hover": { backgroundColor: "#f9f9f9" },
          }}
        >
          <input {...getInputProps()} />
          <CloudUpload sx={{ fontSize: 48, color: "#666", mb: 2 }} />
          <Typography>
            {isDragActive
              ? "Drop the file here"
              : "Drag and drop an Excel/CSV file here, or click to select"}
          </Typography>
          <Typography variant="caption" color="textSecondary">
            Supported formats: .xlsx, .xls, .csv
          </Typography>
        </Box>

        {selectedFile && (
          <Box
            sx={{
              mt: 3,
              p: 2,
              borderRadius: "8px",
              bgcolor: "#f5f5f5",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              border: "1px solid #ddd",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Description color="primary" />
              <Typography variant="body2">{selectedFile.name}</Typography>
            </Box>
            <IconButton size="small" onClick={handleRemoveFile}>
              <Close fontSize="small" />
            </IconButton>
          </Box>
        )}

        {uploading && (
          <Box sx={{ mt: 3 }}>
            <LinearProgress variant="determinate" value={progress} />
            <Typography variant="body2" sx={{ mt: 1 }}>
              Uploading... {progress}%
            </Typography>
          </Box>
        )}

        <Box sx={{ display: "flex", justifyContent: "center", mt: 4, gap: 2 }}>
          <Button variant="outlined" onClick={handleClose}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleUpload}
            disabled={!selectedFile || uploading}
          >
            Save & Upload
          </Button>
        </Box>
      </Box>
    </Modal>
  );
};

export default BulkUploadAssetType;
